import type { ConfidenceRating, ConfidenceLevel } from "./confidenceEngine";

/**
 * Confidence Display — pure presentation mapping for the Confidence Engine.
 * No I/O. Provides display tiers, dynamic styling tokens and short copy so
 * the rating feels like premium sports intelligence rather than gambling
 * noise.
 *
 * Tiers:
 *  - minimal   → tiny inline chip (feeds, cards)
 *  - compact   → standard badge (panels, war room)
 *  - cinematic → large pulsing badge for screenshot mode
 *  - betting   → discreet but credible callout near odds/predictions
 */

export type ConfidenceTier = "minimal" | "compact" | "cinematic" | "betting";

export interface ConfidenceVisual {
  /** Tailwind classes for the wrapper. Uses semantic tokens only. */
  className: string;
  /** Optional secondary animation token (glow / pulse / none). */
  fx: "glow" | "pulse" | "tension" | "muted" | "none";
  /** Short human label tuned per tier. */
  caption: string;
  /** Hue family — useful for dot/bar coloring. */
  hue: "primary" | "emerald" | "amber" | "muted";
}

const HUE_BY_LEVEL: Record<ConfidenceLevel, ConfidenceVisual["hue"]> = {
  limited_sample: "muted",
  low: "muted",
  medium: "primary",
  high: "emerald",
  strong_trend: "emerald",
  volatile: "amber",
};

const FX_BY_LEVEL: Record<ConfidenceLevel, ConfidenceVisual["fx"]> = {
  limited_sample: "muted",
  low: "none",
  medium: "none",
  high: "glow",
  strong_trend: "pulse",
  volatile: "tension",
};

const SHORT_CAPTION: Record<ConfidenceLevel, string> = {
  limited_sample: "Reading match",
  low: "Low signal",
  medium: "Reliable read",
  high: "High signal",
  strong_trend: "Strong trend",
  volatile: "Volatile match",
};

function baseClasses(hue: ConfidenceVisual["hue"], fx: ConfidenceVisual["fx"]): string {
  const hueClass =
    hue === "emerald"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
      : hue === "amber"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
        : hue === "primary"
          ? "border-primary/40 bg-primary/10 text-primary"
          : "border-border/60 bg-muted/30 text-muted-foreground";
  const fxClass =
    fx === "glow"
      ? "shadow-[0_0_18px_-4px_hsl(var(--primary)/0.55)]"
      : fx === "pulse"
        ? "animate-pulse shadow-[0_0_22px_-4px_hsl(var(--primary)/0.55)]"
        : fx === "tension"
          ? "ring-1 ring-amber-500/30"
          : "";
  return `${hueClass} ${fxClass}`.trim();
}

export function getConfidenceVisual(
  rating: ConfidenceRating,
  tier: ConfidenceTier = "compact",
): ConfidenceVisual {
  const hue = HUE_BY_LEVEL[rating.level];
  const fx = FX_BY_LEVEL[rating.level];
  const baseCaption = SHORT_CAPTION[rating.level];
  const pct = Math.round(rating.score * 100);

  if (tier === "minimal") {
    return {
      className: `inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${baseClasses(hue, "none")}`,
      fx: "none",
      caption: baseCaption,
      hue,
    };
  }
  if (tier === "cinematic") {
    return {
      className: `inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-black uppercase tracking-widest ${baseClasses(hue, fx)}`,
      fx,
      caption: `${baseCaption} · ${pct}%`,
      hue,
    };
  }
  if (tier === "betting") {
    return {
      className: `inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-bold tracking-wide ${baseClasses(hue, fx === "pulse" ? "glow" : fx)}`,
      fx: fx === "pulse" ? "glow" : fx,
      caption: `Signal ${pct}%`,
      hue,
    };
  }
  // compact
  return {
    className: `inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-bold ${baseClasses(hue, fx)}`,
    fx,
    caption: `${rating.label} · ${pct}%`,
    hue,
  };
}

/** Whether the rating is solid enough to be highlighted as a "premium" call. */
export function isPremiumConfidence(rating: ConfidenceRating): boolean {
  return rating.level === "high" || rating.level === "strong_trend";
}